import React, { useEffect, useState } from "react";
import { DownloadOutlined, PrinterOutlined } from "@ant-design/icons";
import {
  Alert,
  Button,
  Checkbox,
  Drawer,
  Form,
  Input,
  InputNumber,
  Select,
  Space,
  Spin,
  Tabs,
  message
} from "antd";
import { exportReportDesign, fetchReportDesign, saveReportDesign } from "./report-api";
import ReportBodyEditor from "./report-body-editor";
import { ONLINE_REPORT_TYPES, isLegacyReportType } from "./report-types";

const parseBody = (body) => {
  if (!body) return [];
  if (Array.isArray(body)) return body;
  const parsed = JSON.parse(body);
  return Array.isArray(parsed) ? parsed : [];
};

const downloadJson = (item) => {
  const blob = new Blob([JSON.stringify({ ...item, body: item.body || [] }, null, 2)], {
    type: "application/json"
  });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = `${(item.name || "report").replace(/[\\/:*?"<>|]/g, "_")}.rep`;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
};

const ReportPlaceholdersHelp = () => (
  <Alert
    className="report-design-editor__help"
    type="info"
    showIcon
    message="Типовые подстановки"
    description={
      <div className="report-design-editor__placeholders">
        <span><code>{"{Id}"}</code> — текущая запись</span>
        <span><code>{"{Ids}"}</code> — выбранные записи для SQL <code>IN {"{Ids}"}</code></span>
        <span><code>{"{BegPer}"}</code>, <code>{"{EndPer}"}</code> — период</span>
        <span><code>{"{Part}"}</code>, <code>{"{BranchId}"}</code> — приём пищи и филиал</span>
        <span><code>{"{UserId}"}</code>, <code>{"{UserName}"}</code>, <code>{"{DepId}"}</code> — пользователь</span>
        <span><code>{"{Набор.Поле}"}</code> — значение результата SQL</span>
        <span>
          Форматы: <code>#date</code>, <code>#time</code>, <code>#money</code>, <code>#propis</code>
        </span>
      </div>
    }
  />
);

const ReportDesignEditor = ({ id, open, onClose, onSaved }) => {
  const [form] = Form.useForm();
  const [item, setItem] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState("");
  const isNew = id === 0;
  const readOnly = !isNew && isLegacyReportType(item?.vid);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setError("");
    if (isNew) {
      const next = {
        id: 0,
        name: "",
        description: "",
        vid: ONLINE_REPORT_TYPES[0].value,
        index: 0,
        favorite: false,
        userLevel: 99,
        body: []
      };
      setItem(next);
      form.setFieldsValue(next);
      return;
    }
    setLoading(true);
    fetchReportDesign(id)
      .then((data) => {
        if (cancelled) return;
        const next = { ...data, body: parseBody(data.body) };
        setItem(next);
        form.setFieldsValue(next);
      })
      .catch((e) => {
        if (!cancelled) setError(e.message || "Не удалось загрузить отчёт");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, id, isNew, form]);

  const save = async () => {
    if (readOnly) return;
    try {
      const metadata = await form.validateFields();
      setSaving(true);
      setError("");
      const payload = {
        ...item,
        ...metadata,
        id: isNew ? 0 : item.id,
        body: item.body || []
      };
      const saved = await saveReportDesign(payload, isNew);
      message.success("Отчёт сохранён");
      onSaved?.(saved);
    } catch (e) {
      if (!e?.errorFields) setError(e.message || "Не удалось сохранить отчёт");
    } finally {
      setSaving(false);
    }
  };

  const exportJson = async () => {
    if (isNew || readOnly || !item?.id) return;
    try {
      setExporting(true);
      const report = await exportReportDesign(item.id);
      downloadJson(report);
    } catch (e) {
      message.error(e.message || "Не удалось экспортировать отчёт");
    } finally {
      setExporting(false);
    }
  };

  const metadata = (
    <Form
      form={form}
      layout="vertical"
      disabled={loading || saving || readOnly}
      onValuesChange={(_, values) => setItem((prev) => ({ ...prev, ...values }))}
    >
      <Form.Item
        name="name"
        label="Наименование"
        rules={[{ required: true, message: "Укажите наименование" }]}
      >
        <Input maxLength={200} />
      </Form.Item>
      <Form.Item name="description" label="Описание">
        <Input.TextArea rows={4} maxLength={500} />
      </Form.Item>
      <Form.Item
        name="vid"
        label="Вид отчёта"
        rules={[{ required: true, message: "Укажите вид отчёта" }]}
      >
        <Select options={ONLINE_REPORT_TYPES} showSearch optionFilterProp="label" />
      </Form.Item>
      <Space wrap size={16}>
        <Form.Item name="index" label="Порядок">
          <InputNumber min={0} precision={0} />
        </Form.Item>
        <Form.Item
          name="userLevel"
          label="Максимальный уровень пользователя"
          rules={[{ required: true, message: "Укажите уровень" }]}
        >
          <InputNumber min={0} max={999} precision={0} />
        </Form.Item>
        <Form.Item name="favorite" valuePropName="checked" label=" ">
          <Checkbox>Избранный</Checkbox>
        </Form.Item>
      </Space>
    </Form>
  );

  return (
    <Drawer
      width="min(1000px, 94vw)"
      open={open}
      onClose={onClose}
      destroyOnClose
      rootClassName="report-design-editor"
      title={isNew ? "Новый отчёт" : `Отчёт #${id}`}
      extra={
        <Space>
          {!isNew && !readOnly ? (
            <Button
              icon={<PrinterOutlined />}
              onClick={() => window.open(`/report?reportId=${id}`, "_blank", "noopener,noreferrer")}
            >
              Просмотр
            </Button>
          ) : null}
          <Button
            icon={<DownloadOutlined />}
            disabled={!item || isNew || readOnly}
            loading={exporting}
            onClick={exportJson}
          >
            Экспорт
          </Button>
          <Button type="primary" loading={saving} disabled={readOnly || loading} onClick={save}>
            Сохранить
          </Button>
        </Space>
      }
    >
      {loading ? <Spin /> : null}
      {error ? <Alert type="error" showIcon message={error} style={{ marginBottom: 12 }} /> : null}
      {readOnly ? (
        <Alert
          type="info"
          showIcon
          message="Устаревшие отчёты доступны только для просмотра"
          style={{ marginBottom: 12 }}
        />
      ) : null}
      {item ? (
        <Tabs
          items={[
            { key: "metadata", label: "Основные данные", children: metadata },
            {
              key: "body",
              label: `Элементы (${item.body?.length || 0})`,
              children: (
                <>
                  <ReportPlaceholdersHelp />
                  <ReportBodyEditor
                    value={item.body || []}
                    disabled={readOnly || loading || saving}
                    onChange={(body) => setItem((prev) => ({ ...prev, body }))}
                  />
                </>
              )
            }
          ]}
        />
      ) : null}
    </Drawer>
  );
};

export default ReportDesignEditor;
